import type { ImageBlock } from "@templatical/types";
import type { RenderContext } from "../render-context";
import { escapeAttr } from "../escape";
import { toPaddingString } from "../padding";
import { bgAttr, borderAttr, borderRadiusAttr, heightAttr } from "../utils";
import { isHiddenOnAll, getCssClassAttr } from "../visibility";

/**
 * Render an image block to MJML markup.
 */
export function renderImage(block: ImageBlock, context: RenderContext): string {
  if (isHiddenOnAll(block)) {
    return "";
  }

  if (block.src === "") {
    // Skip rather than emit `<mj-image src="">` which compiles to a
    // broken-image icon. Mirrors video.ts behavior when no source is set.
    return "";
  }

  const padding = toPaddingString(block.styles.padding);
  const bgColor = bgAttr(block.styles.backgroundColor, "container");
  const width =
    block.width === "full" ? context.containerWidth + "px" : block.width + "px";
  const height = heightAttr(block.height);

  const visibilityAttr = getCssClassAttr(block);

  const borderRadiusAttrStr = borderRadiusAttr(block.borderRadius);
  const borderAttrStr = borderAttr(block.border);

  let linkAttr = "";
  if (block.linkUrl) {
    linkAttr = ` href="${escapeAttr(block.linkUrl)}"`;
    if (block.linkOpenInNewTab) {
      linkAttr += ' target="_blank" rel="noopener"';
    }
  }

  const src = escapeAttr(block.src);
  const decorative = block.decorative === true;
  const alt = decorative ? "" : escapeAttr(block.alt);
  const align = block.align;
  const roleAttr = decorative ? ' role="presentation"' : "";

  return `<mj-image
  src="${src}"
  alt="${alt}"
  width="${width}"${height}
  align="${align}"
  padding="${padding}"${bgColor}${borderAttrStr}${borderRadiusAttrStr}${linkAttr}${visibilityAttr}${roleAttr}
/>`;
}
